// PostOnboardingWelcome
//
// The first thing a user sees on first sign-in after their firm is set up.
//
// Behavior is role-aware:
//
//   * Firm founder (Admin/Partner of a brand-new firm with no matters yet):
//     the original two-path welcome — "explore with sample data" OR "add
//     first client". The setup wizard just finished and they need to start
//     putting work into the system.
//
//   * Invited staff (anyone signing in for the first time at an existing
//     firm with matters already in flight): a role-tailored welcome that
//     skips sample data (their firm has real data) and points them at the
//     surfaces they'll actually use day-to-day:
//       - Admin/Partner (joining an existing firm): the dashboard +
//         team management
//       - Attorney/Associate: their assigned matters + time tracking
//       - Paralegal/Clerk: tasks they're assigned to + documents
//       - IT: Settings → Team + the IT onboarding guide
//       - Accountant/Billing Clerk: Billing + trust account guide
//
// Persistence is unchanged — `public.users.welcome_seen_at` (migration 126)
// is the source of truth; localStorage mirrors it to avoid the brief
// re-render flash between RPC fire and userProfile re-fetch.

import React, { useEffect, useState, useMemo } from 'react';
import {
  Sparkles, Briefcase, FileText, UserPlus, Sprout, ArrowRight, X, Check,
  Users, Clock, ClipboardList, Receipt, ShieldCheck, BookOpen,
} from 'lucide-react';
import { useFirm } from '../../contexts/FirmContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRoute } from '../../lib/router';
import { loadSampleData, markOnboardingStepSeen } from '../../lib/db';
import toast from '../../lib/toast';

// Source of truth: public.users.welcome_seen_at (migration 126). Persists
// across redeploys, devices, browsers, and cache wipes. The localStorage
// key below is now just an optimistic mirror so the first render of a
// fresh page doesn't flash the welcome before the profile loads.
const lsKey = (userId) => `lf_welcome_seen_${userId || 'anon'}_v2`;

// Check should-show against the server-side flag on the user profile.
// Falls back to localStorage if the userProfile hasn't loaded yet.
export function shouldShowPostOnboardingWelcome(firm, userProfile) {
  if (!firm || !firm.setup_completed || !firm.id) return false;
  // Server says they've seen it — definitive.
  if (userProfile?.welcome_seen_at) return false;
  // Optimistic localStorage fallback (race between profile load + render).
  if (userProfile?.id) {
    try { if (localStorage.getItem(lsKey(userProfile.id))) return false; }
    catch { /* ignore */ }
  }
  return true;
}

// Cache locally so the next render doesn't flash the welcome between
// "RPC fired" and "userProfile re-fetch with the new flag set".
export function markPostOnboardingWelcomeSeenLocally(userId) {
  try { localStorage.setItem(lsKey(userId), '1'); } catch { /* ignore */ }
}

// Tiny CardOption used by the role-specific layouts. Renders a button
// with icon, label, supporting copy, and a right-arrow flourish.
const CardOption = ({ icon: Icon, accent = 'indigo', label, desc, cta, onClick, disabled }) => {
  const accents = {
    indigo:  { border: 'border-indigo-200 dark:border-indigo-800 hover:border-indigo-400',  bg: 'bg-indigo-600',  text: 'text-indigo-600 dark:text-indigo-300 group-hover:text-indigo-700' },
    emerald: { border: 'border-emerald-200 dark:border-emerald-800 hover:border-emerald-400', bg: 'bg-emerald-600', text: 'text-emerald-600 dark:text-emerald-300 group-hover:text-emerald-700' },
    amber:   { border: 'border-amber-200 dark:border-amber-800 hover:border-amber-400',     bg: 'bg-amber-600',   text: 'text-amber-700 dark:text-amber-300 group-hover:text-amber-800' },
    sky:     { border: 'border-sky-200 dark:border-sky-800 hover:border-sky-400',           bg: 'bg-sky-600',     text: 'text-sky-600 dark:text-sky-300 group-hover:text-sky-700' },
    purple:  { border: 'border-purple-200 dark:border-purple-800 hover:border-purple-400',  bg: 'bg-purple-600',  text: 'text-purple-600 dark:text-purple-300 group-hover:text-purple-700' },
  }[accent] || {};
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group text-left p-5 rounded-2xl border-2 ${accents.border} bg-white dark:bg-slate-800/50 hover:shadow-lg transition disabled:opacity-60 disabled:cursor-wait`}
    >
      <div className={`w-10 h-10 rounded-xl ${accents.bg} flex items-center justify-center mb-3`}>
        <Icon size={20} className="text-white" />
      </div>
      <p className="font-semibold text-slate-900 dark:text-white">{label}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{desc}</p>
      <span className={`mt-3 inline-flex items-center gap-1 text-sm font-medium ${accents.text}`}>
        {cta} <ArrowRight size={14} />
      </span>
    </button>
  );
};

// Map of role → welcome variant. Each variant decides:
//   - the headline + subheading
//   - the two primary CardOptions (one main path, one supporting)
//   - whether to show the "Load sample data" option (only for founders)
function buildVariant({ role, hasExistingFirmData, firstName, firmName }) {
  const greet = firstName ? `Welcome, ${firstName}` : 'Welcome';
  const lowerRole = String(role || '').toLowerCase();

  // Firm founder path — first user, blank firm, kicking the tires.
  // hasExistingFirmData=false means the firm has zero matters yet, which
  // is the strong signal that this is the founder right after the
  // signup wizard.
  if (!hasExistingFirmData && ['admin', 'owner', 'partner'].includes(lowerRole)) {
    return { kind: 'founder',
      headline: `${greet}!`,
      subhead:  `${firmName} is ready to go. Here's the fastest way to start.`,
    };
  }

  // Invited staff at an existing firm — show role-specific guidance.
  if (['admin', 'owner', 'partner'].includes(lowerRole)) {
    return { kind: 'leader',
      headline: `${greet} to ${firmName}`,
      subhead:  `You have full access to the firm. Take a quick look at the dashboard and check that team and settings look right.`,
      primary:   { icon: Briefcase,    accent: 'indigo',  label: 'Open the dashboard',  desc: 'See firm-wide activity, upcoming deadlines, recent matters.', cta: 'Go to dashboard', path: '/' },
      secondary: { icon: Users,        accent: 'emerald', label: 'Team & roles',        desc: 'Review who has access and what their roles are.',                cta: 'Team settings',   path: '/settings?section=team' },
    };
  }

  if (['attorney', 'associate'].includes(lowerRole)) {
    return { kind: 'attorney',
      headline: `${greet} to ${firmName}`,
      subhead:  `You'll spend most of your time on matters and time tracking. Here's where to start.`,
      primary:   { icon: Briefcase, accent: 'indigo',  label: 'My matters',        desc: 'Open the matters you\'re assigned to. Open one to see tasks, deadlines, documents, time entries.', cta: 'View my matters', path: '/matters' },
      secondary: { icon: Clock,     accent: 'emerald', label: 'Track your time',   desc: 'Use the floating timer on any page, or log time after the fact against a specific matter.',       cta: 'Log time',        path: '/time' },
    };
  }

  if (['paralegal', 'clerk'].includes(lowerRole)) {
    return { kind: 'paralegal',
      headline: `${greet} to ${firmName}`,
      subhead:  `You'll be working across tasks, documents, and matter support. The most common starting points:`,
      primary:   { icon: ClipboardList, accent: 'emerald', label: 'My tasks',          desc: 'Tasks assigned to you across all matters. Update status as you work.', cta: 'View my tasks',     path: '/tasks' },
      secondary: { icon: FileText,      accent: 'indigo',  label: 'Documents',         desc: 'Upload, organize, and tag documents for matters you support.',        cta: 'Open documents',    path: '/documents' },
    };
  }

  if (lowerRole === 'it') {
    return { kind: 'it',
      headline: `${greet} — you're the IT contact`,
      subhead:  `Your role is to onboard staff and configure firm settings. You don't have access to client matters or billing — that's intentional, to protect client confidentiality.`,
      primary:   { icon: Users,         accent: 'purple', label: 'Settings → Team',     desc: 'Invite new employees, manage roles, configure access.',          cta: 'Team management', path: '/settings?section=team' },
      secondary: { icon: BookOpen,      accent: 'indigo', label: 'IT onboarding guide', desc: 'Step-by-step playbook in the Help Center. Covers invites, role choice, security baseline, offboarding.', cta: 'Open the guide',  path: '/?help=it-onboarding-full' },
    };
  }

  if (['accountant', 'billing clerk', 'billing'].includes(lowerRole)) {
    return { kind: 'billing',
      headline: `${greet} to ${firmName}`,
      subhead:  `You'll be working with invoices, payments, and the trust account. Most-used surfaces:`,
      primary:   { icon: Receipt,        accent: 'emerald', label: 'Billing dashboard',   desc: 'Invoices, payments, aged receivables across all matters.', cta: 'Open billing',     path: '/billing' },
      secondary: { icon: ShieldCheck,    accent: 'amber',   label: 'Trust account guide', desc: 'How the IOLTA / trust ledger works and the monthly reconciliation flow.', cta: 'Read the guide', path: '/?help=trust-account-deep-dive' },
    };
  }

  // Default fallback — generic staff
  return { kind: 'staff',
    headline: `${greet} to ${firmName}`,
    subhead:  `Your role is set up. Open the dashboard to see what's going on.`,
    primary:   { icon: Briefcase, accent: 'indigo',  label: 'Open the dashboard', desc: 'Activity, deadlines, and assigned work.', cta: 'Go to dashboard', path: '/' },
    secondary: { icon: BookOpen,  accent: 'emerald', label: 'Help Center',        desc: 'Search any topic. Walkthroughs for billing, documents, messaging, and more.', cta: 'Open Help',   path: '/?help=1' },
  };
}

export default function PostOnboardingWelcome({ onDismiss }) {
  const { firm } = useFirm();
  const { userProfile } = useAuth();
  const [, navigate] = useRoute();
  const [loadingSample, setLoadingSample] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Trigger CSS entrance animation after first paint.
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Mark seen the moment this screen renders. We persist to BOTH:
  //   * public.users.welcome_seen_at via SECURITY DEFINER RPC (the source
  //     of truth — survives any client-side state clearing)
  //   * localStorage as an optimistic mirror so the next render in the
  //     same session doesn't briefly re-show the welcome before the
  //     userProfile re-fetch resolves
  useEffect(() => {
    if (!userProfile?.id) return;
    markPostOnboardingWelcomeSeenLocally(userProfile.id);
    markOnboardingStepSeen('welcome').catch(() => { /* best-effort */ });
  }, [userProfile?.id]);

  const dismiss = () => {
    if (userProfile?.id) {
      markPostOnboardingWelcomeSeenLocally(userProfile.id);
      markOnboardingStepSeen('welcome').catch(() => { /* ignore */ });
    }
    onDismiss?.();
  };

  // Pick the variant once per render based on role + firm state. The
  // "hasExistingFirmData" heuristic uses firm.matter_count if available,
  // else falls back to created_at age (>1 day = not brand new).
  const variant = useMemo(() => {
    const firstName = userProfile?.first_name?.trim();
    const firmName  = firm?.name?.trim() || 'your firm';
    const role      = userProfile?.role || '';
    // Best-effort signal that the firm has activity beyond its setup.
    // We don't have a guaranteed counter on `firm`, so use creation age
    // as a proxy: firms older than 24h are assumed to have data.
    const ageMs = firm?.created_at ? Date.now() - new Date(firm.created_at).getTime() : 0;
    const hasExistingFirmData = ageMs > 24 * 60 * 60 * 1000;
    return buildVariant({ role, hasExistingFirmData, firstName, firmName });
  }, [userProfile?.first_name, userProfile?.role, firm?.name, firm?.created_at]);

  const handleSampleData = async () => {
    setLoadingSample(true);
    const { data, error } = await loadSampleData();
    setLoadingSample(false);
    if (error) {
      toast.error('Could not load sample data: ' + error.message);
      return;
    }
    const status = data?.status;
    if (status === 'already_loaded') {
      toast.success('Sample data is already in your account.');
    } else {
      toast.success(`Sample data loaded — ${data?.matters || 2} matters, ${data?.contacts || 2} clients.`);
    }
    dismiss();
    navigate('/matters');
  };

  const handleAddFirstClient = () => {
    dismiss();
    navigate('/contacts');
  };

  const handleSkipToDashboard = () => {
    dismiss();
    navigate('/');
  };

  // Generic handler for role-variant CardOption clicks.
  const handlePath = (path) => {
    dismiss();
    navigate(path);
  };

  const firstName = userProfile?.first_name?.trim();
  const firmName  = firm?.name?.trim() || 'your firm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 p-4">
      <div
        className={[
          'w-full max-w-2xl transition-all duration-700 ease-out',
          mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95',
        ].join(' ')}
      >
        <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {/* Close */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {/* Header ribbon */}
          <div className="relative h-32 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 overflow-hidden">
            {/* Sparkle decoration */}
            <div className="absolute inset-0 opacity-25">
              {Array.from({ length: 18 }).map((_, i) => (
                <Sparkles
                  key={i}
                  size={12 + ((i * 7) % 14)}
                  className="absolute text-white animate-pulse"
                  style={{
                    top:  `${(i * 31) % 100}%`,
                    left: `${(i * 53) % 100}%`,
                    animationDelay: `${(i % 5) * 0.4}s`,
                    animationDuration: '2.4s',
                  }}
                />
              ))}
            </div>
            <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full bg-white dark:bg-slate-900 shadow-lg flex items-center justify-center border-4 border-white dark:border-slate-900">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                <Check size={36} className="text-white" strokeWidth={3} />
              </div>
            </div>
          </div>

          <div className="px-8 pt-16 pb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 dark:text-white">
              {variant.kind === 'founder'
                ? (firstName ? `Welcome, ${firstName}!` : 'You\'re all set up!')
                : variant.headline}
            </h1>
            <p className="text-center text-slate-500 dark:text-slate-400 mt-2 text-sm sm:text-base">
              {variant.kind === 'founder'
                ? `${firmName} is ready to go. Here's the fastest way to start.`
                : variant.subhead}
            </p>

            {/* Founder path — sample data vs. real client */}
            {variant.kind === 'founder' && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <CardOption
                  icon={Sprout}
                  accent="indigo"
                  label={loadingSample ? 'Loading…' : 'Explore with sample data'}
                  desc="Two clients, two matters, a sample invoice & receipt — fully editable, wipeable in one click."
                  cta="Load sample data"
                  onClick={handleSampleData}
                  disabled={loadingSample}
                />
                <CardOption
                  icon={UserPlus}
                  accent="emerald"
                  label="Add your first client"
                  desc="Jump straight to the contact form. You can open a matter for them next."
                  cta="Add client"
                  onClick={handleAddFirstClient}
                />
              </div>
            )}

            {/* Invited / role-specific path */}
            {variant.kind !== 'founder' && variant.primary && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <CardOption
                  icon={variant.primary.icon}
                  accent={variant.primary.accent}
                  label={variant.primary.label}
                  desc={variant.primary.desc}
                  cta={variant.primary.cta}
                  onClick={() => handlePath(variant.primary.path)}
                />
                {variant.secondary && (
                  <CardOption
                    icon={variant.secondary.icon}
                    accent={variant.secondary.accent}
                    label={variant.secondary.label}
                    desc={variant.secondary.desc}
                    cta={variant.secondary.cta}
                    onClick={() => handlePath(variant.secondary.path)}
                  />
                )}
              </div>
            )}

            {/* What's in DocketFlow — small reassurance band */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
              <Feature icon={Briefcase} label="Matters" />
              <Feature icon={FileText}  label="Documents" />
              <Feature icon={Sparkles}  label="Client portal" />
            </div>

            {/* Skip */}
            <div className="mt-6 text-center">
              <button
                onClick={handleSkipToDashboard}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition"
              >
                Skip — take me to the dashboard
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          {variant.kind === 'founder'
            ? 'Tip: every page has a setup checklist at the top to guide you through the rest.'
            : variant.kind === 'it'
            ? 'Tip: the ? button (bottom-right of any page) opens the in-app Help Center, including the full IT category.'
            : 'Tip: the ? button (bottom-right of any page) opens the in-app Help Center.'}
        </p>
      </div>
    </div>
  );
}

const Feature = ({ icon: Icon, label }) => (
  <div className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400">
    <Icon size={12} />
    <span>{label}</span>
  </div>
);
